import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { MessageService } from 'primeng/api';
import { pipe, switchMap, tap } from 'rxjs';
import { SelectChallengeService } from 'src/app/core/services/select-challenge.service';
import { PSL } from '../../../../../core/common/models/master-data/psl';
import {
  ChallengesState,
  PairChallenge,
  SubChallenge,
} from './challenges-sub-challenges.model';

const initialSubChallenge = {
  name: '',
  description: '',
  isActive: false,
  isValid: false,
  id: '',
};

const initialState: ChallengesState = {
  initialChallenges: [],
  initialSubChallenge,
  initialChallengeResult: initialSubChallenge,
  selectedPSL: {
    name: '',
    id: '',
  },
  pairChallenges: [],
  selectedSubChallenges: [],
  isLoading: false,
};

export type ChallengesSubChallengesStoreType = InstanceType<
  typeof ChallengesSubChallengesStore
>;

export const ChallengesSubChallengesStore = signalStore(
  { providedIn: 'root' },
  withDevtools('challenges'),
  withState(initialState),
  withMethods(
    (
      store,
      selectChallengeService = inject(SelectChallengeService),
      toastr = inject(MessageService),
    ) => ({
      loadActiveSubAndChallenge: rxMethod<void>(
        pipe(
          switchMap(() => selectChallengeService.getChallenges()),
          tapResponse(
            (activeChallenges) => {
              patchState(store, {
                initialChallenges: activeChallenges,
              });
            },
            (error: HttpErrorResponse) => {
              console.error(
                'An error occurred in loadActiveSubAndChallenge:',
                error,
              );
            },
          ),
        ),
      ),
      loadChallengeByPSL: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap((selectedPslId: string) =>
            selectChallengeService.getChallengesByPSL(selectedPslId),
          ),
          tapResponse(
            (challengesByPSL) => {
              patchState(store, {
                isLoading: false,
                initialChallenges: challengesByPSL,
              });
            },
            (error: HttpErrorResponse) => {
              console.error('An error occurred in loadChallengeByPSL:', error);
              toastr.add({
                severity: 'error',
                summary: 'Error',
                detail: `${error.message}. Please contact administrator!`,
                closable: false,
              });
              patchState(store, {
                isLoading: false,
              });
            },
          ),
        ),
      ),
      setSelectedPSL(selectedPSL: PSL) {
        patchState(store, { selectedPSL });
      },
      addPairChallenge(pairChallenge: PairChallenge) {
        patchState(store, {
          pairChallenges: [...store.pairChallenges(), pairChallenge],
          selectedSubChallenges: store.selectedSubChallenges().map((item) => ({
            ...item,
            isDisabled:
              item.id === pairChallenge.subChallengeId ? true : item.isDisabled,
          })),
        });
      },
      removePairChallenge(pairChallenge: PairChallenge) {
        patchState(store, {
          pairChallenges: store
            .pairChallenges()
            .filter(
              (challenge) =>
                challenge.subChallengeId !== pairChallenge.subChallengeId,
            ),
          selectedSubChallenges: store.selectedSubChallenges().map((item) => ({
            ...item,
            isDisabled:
              item.id === pairChallenge.subChallengeId
                ? false
                : item.isDisabled,
          })),
        });
      },
      clearPairChallenge() {
        patchState(store, {
          pairChallenges: [],
          selectedSubChallenges: store
            .selectedSubChallenges()
            .map((item) => ({ ...item, isDisabled: false })),
        });
      },
      setSelectedSubChallenges(subChallenges: SubChallenge[]) {
        const activeSubChallenges = subChallenges.filter((s) => s.isActive);
        patchState(store, (state) => ({
          selectedSubChallenges: activeSubChallenges.map((item) => ({
            ...item,
            isDisabled: !!state.pairChallenges.find(
              (pair) => pair.subChallengeId === item.id,
            ),
          })),
        }));
      },
      clearStore() {
        patchState(store, initialState);
      },
    }),
  ),
);
